import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtINR } from "@/lib/api";
import { calculateCareerRoi, formatCareerRoi, formatBreakeven } from "@/lib/careerRoi";
import { useAuth } from "@/lib/AuthContext";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { Bookmark, Check, Lock, LayoutGrid, Table as TableIcon, BarChart3, X, ChevronDown } from "lucide-react";

const DEMO_SLUGS = ["university-of-melbourne", "delft-university-of-technology"];
const MAX_SELECT = 4;
const VIEW_OPTIONS = [
  { id: "cards", label: "Cards", icon: LayoutGrid },
  { id: "table", label: "Table", icon: TableIcon },
  { id: "stacks", label: "Stacked Bars", icon: BarChart3 },
];
const SORT_OPTIONS = [
  { id: "grand_total", label: "Total cost" },
  { id: "total_tuition", label: "Tuition" },
  { id: "total_living", label: "Living" },
  { id: "one_time_total", label: "One-time costs" },
];

const PALETTE = {
  tuition: "#4F46E5", // indigo
  living:  "#10B981", // emerald
  oneTime: "#F59E0B", // amber
};

export default function Financial() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [all, setAll] = useState([]);
  const [slugs, setSlugs] = useState(() => {
    if (!user) return DEMO_SLUGS;
    const ss = params.get("slugs");
    if (ss) return ss.split(",").filter(Boolean).slice(0, MAX_SELECT);
    const s = params.get("slug");
    return s ? [s] : [];
  });
  const [years, setYears] = useState(parseInt(params.get("years") || "3", 10));
  const [inflation, setInflation] = useState(parseFloat(params.get("inflation") || "5"));
  const [plans, setPlans] = useState({}); // { slug: planData }
  const [sortBy, setSortBy] = useState("grand_total");
  const [uniPickerOpen, setUniPickerOpen] = useState(true);
  const [view, setView] = useState("stacks");
  const [saving, setSaving] = useState(false);
  const [savedTick, setSavedTick] = useState(false);

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/universities");
      setAll(data);
    })();
  }, []);

  // Fetch plans for the selected slugs (parallel)
  useEffect(() => {
    if (slugs.length === 0) { setPlans({}); return; }
    let cancelled = false;
    (async () => {
      const results = await Promise.all(slugs.map(s =>
        api.post("/financial-plan", { university_slug: s, duration_years: years, inflation_pct: inflation })
          .then(r => [s, r.data]).catch(() => [s, null])
      ));
      if (cancelled) return;
      setPlans(Object.fromEntries(results.filter(([, v]) => v)));
    })();
    return () => { cancelled = true; };
  }, [slugs, years, inflation]);

  const toggleSlug = (s) => {
    if (!user) { startGoogle(); return; }
    setSlugs(curr => {
      if (curr.includes(s)) return curr.filter(x => x !== s);
      if (curr.length >= MAX_SELECT) return curr;
      return [...curr, s];
    });
  };

  const sortedPlans = useMemo(() => {
    return slugs
      .map(s => plans[s])
      .filter(Boolean)
      .slice()
      .sort((a, b) => (a[sortBy] || 0) - (b[sortBy] || 0));
  }, [slugs, plans, sortBy]);

  const summary = useMemo(() => {
    if (sortedPlans.length === 0) return null;
    const totals = sortedPlans.map(p => p.grand_total);
    const cheapest = sortedPlans[0];
    const dearest = sortedPlans[sortedPlans.length - 1];
    const avg = totals.reduce((a, b) => a + b, 0) / totals.length;
    const savings = dearest.grand_total - cheapest.grand_total;
    const bestRoi = sortedPlans.slice().sort((a, b) => (roiMultiple(b) || 0) - (roiMultiple(a) || 0))[0];
    return { cheapest, dearest, avg, savings, bestRoi };
  }, [sortedPlans]);

  const maxTotal = useMemo(() => Math.max(1, ...sortedPlans.map(p => p.grand_total)), [sortedPlans]);

  const handleSave = async () => {
    if (!user || slugs.length === 0) return;
    setSaving(true);
    try {
      await Promise.all(slugs.map(s => api.post("/saved-plans", { university_slug: s, duration_years: years, inflation_pct: inflation })));
      setSavedTick(true);
      setTimeout(() => setSavedTick(false), 2200);
    } finally {
      setSaving(false);
    }
  };

  if (!all.length) return null;

  return (
    <div className="min-h-screen bg-white lg:pl-[240px]">
      <Nav />
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-12">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-micro font-semibold uppercase tracking-[0.05em] text-[var(--s500)]">Investment Intelligence</span>
            </div>
            <h1 className="mt-3 text-h1 text-[var(--s900)]" data-testid="fin-headline">
              The real cost of every choice, <em className="italic text-[var(--ind)]">line by line.</em>
            </h1>
          </div>
          {user && slugs.length > 0 && (
            <button
              onClick={handleSave}
              disabled={saving}
              data-testid="fin-save"
              className={`inline-flex items-center gap-2 text-body-sm font-medium px-4 py-2.5 rounded-[var(--r8)] border transition-colors ${
                savedTick ? "bg-emerald-50 text-[var(--success)] border-emerald-300" : "bg-white text-[var(--ind)] border-[var(--ind)] hover:bg-[var(--ind-soft)]"
              }`}
            >
              {savedTick ? <Check size={15} strokeWidth={2.4} /> : <Bookmark size={15} strokeWidth={2} />}
              {savedTick ? "Saved" : (saving ? "Saving…" : `Save ${slugs.length} plan${slugs.length > 1 ? "s" : ""}`)}
            </button>
          )}
        </div>
        <p className="text-body text-[var(--s600)] mt-3 mb-8 max-w-xl leading-relaxed">
          Pick up to {MAX_SELECT} universities. Every figure is computed from the same set of assumptions — labelled and visible.
        </p>

        {/* Controls */}
        <div className="card p-6 mb-8">
          <button
            type="button"
            onClick={() => setUniPickerOpen((v) => !v)}
            aria-expanded={uniPickerOpen}
            data-testid="fin-picker-toggle"
            className="w-full flex items-center justify-between gap-3 flex-wrap text-left"
          >
            <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s500)] flex items-center gap-1.5">
              Universities {!user && <Lock size={11} strokeWidth={2.2} className="text-[var(--s400)]" />}
              <span className="text-[var(--s400)] tracking-normal normal-case">· {slugs.length}/{MAX_SELECT} selected</span>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {slugs.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {slugs.map(s => {
                    const u = all.find(x => x.slug === s);
                    return (
                      <span key={s} className="inline-flex items-center gap-1.5 text-label bg-[var(--ind-soft)] text-[var(--ind)] border border-[var(--ind)] px-2.5 py-1 rounded-[var(--r8)]">
                        {u?.name || s}
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleSlug(s); }}
                          disabled={!user}
                          className="hover:opacity-70 disabled:cursor-not-allowed"
                        >
                          <X size={11} strokeWidth={2.6} />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              <ChevronDown
                size={18}
                strokeWidth={2}
                className="text-[var(--s500)] transition-transform"
                style={{ transform: uniPickerOpen ? "rotate(180deg)" : "rotate(0)" }}
              />
            </div>
          </button>

          {uniPickerOpen && (
            <div className="flex flex-wrap gap-2 max-h-44 overflow-y-auto pr-2 mt-4 mb-6">
              {all.map(u => {
                const on = slugs.includes(u.slug);
                const disabled = !user || (!on && slugs.length >= MAX_SELECT);
                return (
                  <button
                    key={u.slug}
                    onClick={() => toggleSlug(u.slug)}
                    disabled={disabled}
                    data-testid={`fin-pick-${u.slug}`}
                    title={!user ? "Sign in to compare any universities" : undefined}
                    className={`text-body-sm px-3 py-1.5 rounded-[var(--r8)] border transition-colors ${
                      on
                        ? "bg-[var(--ind)] text-white border-[var(--ind)]"
                        : disabled
                          ? "bg-white text-[var(--s400)] border-[var(--s200)] cursor-not-allowed"
                          : "bg-white text-[var(--s700)] border-[var(--s200)] hover:border-[var(--s400)]"
                    }`}
                  >
                    {u.name}
                  </button>
                );
              })}
            </div>
          )}

          {/* Sliders + view + sort */}
          <div className="grid md:grid-cols-2 gap-6 pt-5 border-t border-[var(--s200)]">
            <Slider label={`Duration · ${years} years`} value={years} min={2} max={5} step={1} onChange={setYears} />
            <Slider label={`Inflation · ${inflation.toFixed(1)}%`} value={inflation} min={0} max={10} step={0.5} onChange={setInflation} />
          </div>
          <div className="grid md:grid-cols-2 gap-6 mt-5">
            <div>
              <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s500)] mb-2">Sort by</div>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)} data-testid="fin-sortby">
                {SORT_OPTIONS.map(s => <option key={s.id} value={s.id}>{s.label} — ascending</option>)}
              </select>
            </div>
            <div>
              <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s500)] mb-2">View</div>
              <div className="inline-flex border border-[var(--s200)] rounded-[var(--r8)] p-0.5 bg-white">
                {VIEW_OPTIONS.map(v => (
                  <button
                    key={v.id}
                    onClick={() => setView(v.id)}
                    data-testid={`fin-view-${v.id}`}
                    className={`inline-flex items-center gap-1.5 text-label px-3 py-1.5 rounded transition-colors ${
                      view === v.id ? "bg-[var(--ind)] text-white" : "text-[var(--s600)] hover:text-[var(--ind)]"
                    }`}
                  >
                    <v.icon size={13} strokeWidth={2} /> {v.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {sortedPlans.length === 0 ? (
          <div className="card p-10 text-center text-[var(--s500)]">
            Pick at least one university above to start comparing.
          </div>
        ) : (
          <div className="relative">
            <div className={!user ? "blur-[6px] select-none pointer-events-none" : ""}>
              {/* Summary KPI cards */}
              {summary && sortedPlans.length > 1 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  <KPI label="Cheapest" value={`${fmtINR(summary.cheapest.grand_total)} · ${aiConfidence(summary.cheapest)}%`} note={summary.cheapest.university?.name} accent="emerald" />
                  <KPI label="Most expensive" value={`${fmtINR(summary.dearest.grand_total)} · ${aiConfidence(summary.dearest)}%`} note={summary.dearest.university?.name} accent="red" />
                  <KPI label="Best career ROI" value={fmtROI(summary.bestRoi)} note={`${summary.bestRoi.university?.name} · breakeven ${fmtBreakeven(summary.bestRoi)}`} accent="ind" />
                  <KPI label="Max savings" value={fmtINR(summary.savings)} note="cheapest vs dearest" />
                </div>
              )}

              {/* Comparison sections — side-by-side in stacks view only */}
              {view === "stacks" ? (
                <>
                  <div className="grid lg:grid-cols-2 gap-8">
                    <div>
                      <SectionHeader title="University comparison" note={`${years}-yr total · ${inflation}% inflation`} />
                      <StacksView plans={sortedPlans} maxTotal={maxTotal} />
                    </div>
                    <div>
                      <SectionHeader title="Living expenses breakdown · per year" note="Year-1 figures, before inflation" />
                      <LivingStacksView plans={sortedPlans} />
                    </div>
                  </div>
                  <p className="text-micro text-[var(--s400)] mt-3 leading-relaxed max-w-3xl">
                    Career ROI = (median graduate annual salary × 10-year working horizon) ÷ total {years}-year programme investment, expressed as a multiple.
                    Breakeven = total investment ÷ median graduate salary. Median salary is the university&apos;s own published graduate-outcomes figure; &quot;—&quot; shown when not publicly available.
                    Single formula used everywhere (Financial, Decision Report, AI Recommendations).
                  </p>
                </>
              ) : (
                <>
                  <SectionHeader title="University comparison" note={`${years}-yr total · ${inflation}% inflation`} />
                  {view === "cards" && <CardsView plans={sortedPlans} maxTotal={maxTotal} />}
                  {view === "table" && <TableView plans={sortedPlans} />}
                  <p className="text-micro text-[var(--s400)] mt-3 leading-relaxed max-w-3xl">
                    Career ROI = (median graduate annual salary × 10-year working horizon) ÷ total {years}-year programme investment, expressed as a multiple.
                    Breakeven = total investment ÷ median graduate salary. Median salary is the university&apos;s own published graduate-outcomes figure; &quot;—&quot; shown when not publicly available.
                    Single formula used everywhere (Financial, Decision Report, AI Recommendations).
                  </p>

                  <SectionHeader title="Living expenses breakdown · per year" note="Year-1 figures, before inflation" className="mt-12" />
                  {view === "cards" && <LivingCardsView plans={sortedPlans} />}
                  {view === "table" && <LivingTableView plans={sortedPlans} />}
                </>
              )}
            </div>

            {!user && (
              <div className="absolute inset-0 flex items-start justify-center pt-24 px-4">
                <div className="text-center max-w-md bg-white/85 backdrop-blur-md border border-[var(--s200)] rounded-[var(--r20)] shadow-xl p-8">
                  <div className="w-12 h-12 mx-auto rounded-full bg-[var(--ind-soft)] text-[var(--ind)] flex items-center justify-center mb-4">
                    <Lock size={20} strokeWidth={1.8} />
                  </div>
                  <h3 className="text-h2 font-semibold text-[var(--s900)] leading-tight">Sign in to reveal the numbers</h3>
                  <p className="text-body-sm text-[var(--s600)] mt-2 leading-relaxed">
                    Full financial comparison across up to 4 universities — tuition, living, one-time, and a per-year living breakdown.
                  </p>
                  <button onClick={startGoogle} data-testid="fin-unlock" className="btn-primary mt-5 inline-flex items-center justify-center gap-2">
                    Get Early Access
                  </button>
                  <div className="mt-4 text-micro text-[var(--s500)] space-y-1">
                    <div><span className="text-[var(--success)] font-semibold">✓</span> Sign in securely with Google</div>
                    <div><span className="text-[var(--success)] font-semibold">✓</span> Free until October 30</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

/* -------- helpers / sub-components -------- */

// AI confidence (%) — deterministic blend of QS rank, scholarship availability and inflation pressure.
function aiConfidence(plan) {
  const u = plan.university || {};
  let c = 95;
  c -= Math.max(0, (u.qs_rank_2025 || 100) - 30) * 0.08;
  if (!u.scholarships_available) c -= 4;
  return Math.max(78, Math.min(97, Math.round(c)));
}

// Scholarship amount range based on the university's reported availability + selectivity.
function scholarshipRange(plan) {
  const u = plan.university || {};
  const totalTuition = plan.total_tuition || 0;
  let lo = 0, hi = 0.1;
  if (u.scholarships_available) {
    if ((u.ib_score_median || 0) >= 39) { lo = 0.25; hi = 0.45; }
    else if ((u.ib_score_median || 0) >= 36) { lo = 0.20; hi = 0.35; }
    else { lo = 0.15; hi = 0.30; }
  }
  const loAmt = totalTuition * lo;
  const hiAmt = totalTuition * hi;
  if (hiAmt < 50000) return "Limited";
  return `${fmtINR(loAmt)} – ${fmtINR(hiAmt)}`;
}

// ---- Career ROI helpers (per-plan wrappers around the canonical formula) ----
// Career ROI is calculated EXCLUSIVELY in /app/frontend/src/lib/careerRoi.js.
// We do NOT define another formula here.
function fmtROI(plan) {
  return formatCareerRoi({
    medianGraduateSalaryInr: plan.university?.median_graduate_salary_inr,
    totalInvestmentInr: plan.grand_total,
  });
}

function fmtBreakeven(plan) {
  return formatBreakeven({
    medianGraduateSalaryInr: plan.university?.median_graduate_salary_inr,
    totalInvestmentInr: plan.grand_total,
  });
}

function roiMultiple(plan) {
  return calculateCareerRoi({
    medianGraduateSalaryInr: plan.university?.median_graduate_salary_inr,
    totalInvestmentInr: plan.grand_total,
  }).multiple;
}

function Slider({ label, value, min, max, step, onChange }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s500)]">{label}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        className="w-full accent-[var(--ind)]"
      />
    </div>
  );
}

function SectionHeader({ title, note, className = "" }) {
  return (
    <div className={`flex items-baseline justify-between gap-3 mb-4 ${className}`}>
      <h2 className="text-h2 md:text-h2 font-semibold text-[var(--s900)] leading-tight">{title}</h2>
      <span className="text-micro text-[var(--s500)]">{note}</span>
    </div>
  );
}

function KPI({ label, value, note, accent }) {
  const valCls = accent === "emerald" ? "text-[var(--success)]" : accent === "red" ? "text-[var(--danger)]" : accent === "ind" ? "text-[var(--ind)]" : "text-[var(--s900)]";
  return (
    <div className="card p-4">
      <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s500)]">{label}</div>
      <div className={`text-h3 md:text-h2 font-semibold ${valCls} mt-1 leading-tight`}>{value}</div>
      <div className="text-micro text-[var(--s500)] mt-1 truncate">{note}</div>
    </div>
  );
}

function rankBadge(idx, total) {
  if (idx === 0 && total > 1) return { label: "Lowest cost", cls: "bg-emerald-50 text-[var(--success)] border-emerald-200" };
  if (idx === total - 1 && total > 1) return { label: "Highest cost", cls: "bg-red-50 text-[var(--danger)] border-red-200" };
  return null;
}

function CardsView({ plans, maxTotal }) {
  return (
    <div className="grid md:grid-cols-2 gap-5">
      {plans.map((p, i) => {
        const badge = rankBadge(i, plans.length);
        const border = i === 0 && plans.length > 1 ? "border-emerald-300" : i === plans.length - 1 && plans.length > 1 ? "border-red-300" : "border-[var(--s200)]";
        const pct = (p.grand_total / maxTotal) * 100;
        return (
          <div key={p.university.slug} className={`relative rounded-[var(--r12)] bg-white border ${border} p-6 transition-all hover:-translate-y-0.5 hover:shadow-md`}>
            {badge && (
              <span className={`absolute top-3 right-3 text-micro font-semibold tracking-[0.05em] uppercase px-2 py-1 rounded-[var(--r8)] border ${badge.cls}`}>{badge.label}</span>
            )}
            <div className="text-h3 font-semibold text-[var(--s900)] leading-tight">{p.university.name}</div>
            <div className="text-micro text-[var(--s500)] mt-0.5">{p.university.city} · {p.university.country}</div>

            <div className="mt-4">
              <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s400)]">Grand total</div>
              <div className="flex items-baseline gap-2 mt-1">
                <div className="text-h1 font-semibold text-[var(--ind)] leading-none">{fmtINR(p.grand_total)}</div>
                <span className="text-label font-semibold text-[var(--s500)]">({aiConfidence(p)}% AI conf.)</span>
              </div>
              <div className="mt-3 h-2 rounded-full bg-[var(--s100)] overflow-hidden">
                <div className="h-full bg-[var(--ind)] rounded-full" style={{ width: `${pct}%` }} />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mt-5 pt-4 border-t border-[var(--s200)]">
              <MiniStat k="Tuition" v={fmtINR(p.total_tuition)} />
              <MiniStat k="Living" v={fmtINR(p.total_living)} />
              <MiniStat k="One-time" v={fmtINR(p.one_time_total)} />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <MiniStat k="Career ROI" v={fmtROI(p)} testid={`fin-roi-${p.university.slug}`} />
              <MiniStat k="Breakeven" v={fmtBreakeven(p)} testid={`fin-be-${p.university.slug}`} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TableView({ plans }) {
  const rows = [
    ["Grand total", p => `${fmtINR(p.grand_total)} (${aiConfidence(p)}% AI conf.)`, true],
    ["Tuition (total)", p => fmtINR(p.total_tuition)],
    ["Living (total)", p => fmtINR(p.total_living)],
    ["One-time costs", p => fmtINR(p.one_time_total)],
    ["Scholarships (range)", p => scholarshipRange(p)],
    ["Career ROI", p => fmtROI(p)],
    ["Breakeven", p => fmtBreakeven(p)],
  ];
  return (
    <div className="card overflow-x-auto">
      <table className="w-full text-body-sm" style={{ borderCollapse: "collapse" }}>
        <thead>
          <tr className="border-b border-[var(--s200)] bg-[var(--bg)]">
            <th className="text-left p-4 text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s500)] w-44"></th>
            {plans.map(p => (
              <th key={p.university.slug} className="text-left p-4">
                <div className="text-body font-semibold text-[var(--s900)] leading-tight">{p.university.name}</div>
                <div className="text-micro text-[var(--s500)] mt-0.5">{p.university.city}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, fn, emph], i) => (
            <tr key={i} className="border-b border-[var(--s200)] last:border-0">
              <td className="p-4 text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s500)]">{label}</td>
              {plans.map((p, idx) => {
                const isMin = emph && idx === 0;
                const isMax = emph && idx === plans.length - 1 && plans.length > 1;
                const cls = isMin ? "text-[var(--success)] font-semibold" : isMax ? "text-[var(--danger)] font-semibold" : emph ? "font-semibold text-[var(--s900)]" : "text-[var(--s800)]";
                return <td key={p.university.slug} className={`p-4 ${cls}`}>{fn(p)}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StacksView({ plans, maxTotal }) {
  return (
    <div className="card p-6 space-y-5">
      <Legend items={[["Tuition", PALETTE.tuition], ["Living", PALETTE.living], ["One-time", PALETTE.oneTime]]} />
      {plans.map((p, idx) => {
        const tPct = (p.total_tuition / maxTotal) * 100;
        const lPct = (p.total_living / maxTotal) * 100;
        const oPct = (p.one_time_total / maxTotal) * 100;
        const totalPct = tPct + lPct + oPct;
        const badge = rankBadge(idx, plans.length);
        return (
          <div key={p.university.slug}>
            <div className="flex justify-between items-baseline mb-2">
              <div className="flex items-center gap-2">
                <div className="text-body font-semibold text-[var(--s900)]">{p.university.name}</div>
                {badge && <span className={`text-micro font-semibold tracking-[0.05em] uppercase px-1.5 py-0.5 rounded border ${badge.cls}`}>{badge.label}</span>}
              </div>
              <div className="flex items-baseline gap-1.5">
                <div className="text-body font-semibold text-[var(--s900)]">{fmtINR(p.grand_total)}</div>
                <span className="text-micro font-semibold text-[var(--s500)]">({aiConfidence(p)}%)</span>
              </div>
            </div>
            <div className="flex h-6 rounded-[var(--r8)] overflow-hidden bg-[var(--s100)]" style={{ width: `${Math.max(totalPct, 8)}%` }}>
              <div style={{ width: `${(tPct / totalPct) * 100}%`, background: PALETTE.tuition }} title={`Tuition · ${fmtINR(p.total_tuition)}`} />
              <div style={{ width: `${(lPct / totalPct) * 100}%`, background: PALETTE.living }} title={`Living · ${fmtINR(p.total_living)}`} />
              <div style={{ width: `${(oPct / totalPct) * 100}%`, background: PALETTE.oneTime }} title={`One-time · ${fmtINR(p.one_time_total)}`} />
            </div>
            <div className="mt-1.5 flex items-center gap-4 text-micro text-[var(--s500)]" data-testid={`fin-roi-row-${p.university.slug}`}>
              <span>Career ROI · <span className="text-[var(--s800)] font-semibold">{fmtROI(p)}</span></span>
              <span>Breakeven · <span className="text-[var(--s800)] font-semibold">{fmtBreakeven(p)}</span></span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MiniStat({ k, v, testid }) {
  return (
    <div {...(testid ? { "data-testid": testid } : {})}>
      <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s400)]">{k}</div>
      <div className="text-body-sm font-semibold text-[var(--s900)] mt-0.5 truncate">{v}</div>
    </div>
  );
}

function Legend({ items }) {
  return (
    <div className="flex flex-wrap gap-4 text-label text-[var(--s600)] pb-2 border-b border-[var(--s100)]">
      {items.map(([label, color]) => (
        <span key={label} className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-[var(--r8)]" style={{ background: color }} /> {label}
        </span>
      ))}
    </div>
  );
}

/* -------- LIVING breakdown views -------- */

function getCategories(plans) {
  // union of category keys across all selected plans
  const map = new Map();
  plans.forEach(p => {
    (p.living_breakdown?.categories || []).forEach(c => {
      if (!map.has(c.key)) map.set(c.key, c.label);
    });
  });
  return Array.from(map.entries()).map(([key, label]) => ({ key, label }));
}

function getCategoryAmount(plan, key) {
  const c = (plan.living_breakdown?.categories || []).find(x => x.key === key);
  return c?.amount || 0;
}

function LivingCardsView({ plans }) {
  const cats = getCategories(plans);
  return (
    <div className="grid md:grid-cols-2 gap-5">
      {plans.map((p) => {
        const total = p.living_breakdown?.total || cats.reduce((a, c) => a + getCategoryAmount(p, c.key), 0);
        return (
          <div key={p.university.slug} className="card p-6">
            <div className="text-body-lg font-semibold text-[var(--s900)] leading-tight">{p.university.name}</div>
            <div className="text-micro text-[var(--s500)] mt-0.5">{p.university.city}</div>
            <div className="text-h2 font-semibold text-[var(--ind)] mt-3">{fmtINR(total)} <span className="text-label text-[var(--s500)] font-medium">· per year</span></div>
            <div className="mt-4 space-y-2.5">
              {cats.map(c => {
                const amt = getCategoryAmount(p, c.key);
                const pct = total ? (amt / total) * 100 : 0;
                return (
                  <div key={c.key}>
                    <div className="flex justify-between text-body-sm mb-1">
                      <span className="text-[var(--s700)]">{c.label}</span>
                      <span className="font-semibold text-[var(--s900)]">{fmtINR(amt)}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-[var(--s100)] overflow-hidden">
                      <div className="h-full bg-[var(--ind)] rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function LivingTableView({ plans }) {
  const cats = getCategories(plans);
  return (
    <div className="card overflow-x-auto">
      <table className="w-full text-body-sm" style={{ borderCollapse: "collapse" }}>
        <thead>
          <tr className="border-b border-[var(--s200)] bg-[var(--bg)]">
            <th className="text-left p-4 text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s500)] w-56">Category (per year)</th>
            {plans.map(p => (
              <th key={p.university.slug} className="text-left p-4 text-body-sm font-semibold text-[var(--s900)]">{p.university.name}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {cats.map(c => (
            <tr key={c.key} className="border-b border-[var(--s200)]">
              <td className="p-4 text-body-sm text-[var(--s700)]">{c.label}</td>
              {plans.map(p => <td key={p.university.slug} className="p-4 text-body-sm font-medium text-[var(--s800)]">{fmtINR(getCategoryAmount(p, c.key))}</td>)}
            </tr>
          ))}
          <tr className="bg-[var(--bg)]">
            <td className="p-4 text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s500)]">Total / year</td>
            {plans.map(p => {
              const total = p.living_breakdown?.total || 0;
              return <td key={p.university.slug} className="p-4 text-body font-semibold text-[var(--ind)]">{fmtINR(total)}</td>;
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function LivingStacksView({ plans }) {
  const cats = getCategories(plans);
  const colors = [
    "#4F46E5", // Rent (annual)        — indigo
    "#10B981", // Food & Groceries     — emerald
    "#F59E0B", // Transport            — amber
    "#0EA5E9", // Phone + Internet     — sky
    "#F43F5E", // Personal / Misc      — rose
    "#8B5CF6", // Travel flights       — violet
  ];
  const maxTotal = Math.max(1, ...plans.map(p => p.living_breakdown?.total || 0));
  return (
    <div className="card p-6 space-y-5">
      <Legend items={cats.map((c, i) => [c.label, colors[i % colors.length]])} />
      {plans.map(p => {
        const total = p.living_breakdown?.total || 0;
        const pct = (total / maxTotal) * 100;
        return (
          <div key={p.university.slug}>
            <div className="flex justify-between items-baseline mb-2">
              <div className="text-body font-semibold text-[var(--s900)]">{p.university.name}</div>
              <div className="text-body-sm font-semibold text-[var(--s900)]">{fmtINR(total)} <span className="text-micro text-[var(--s500)] font-medium">· /yr</span></div>
            </div>
            <div className="flex h-6 rounded-[var(--r8)] overflow-hidden bg-[var(--s100)]" style={{ width: `${Math.max(pct, 8)}%` }}>
              {cats.map((c, i) => {
                const amt = getCategoryAmount(p, c.key);
                const w = total ? (amt / total) * 100 : 0;
                return <div key={c.key} style={{ width: `${w}%`, background: colors[i % colors.length] }} title={`${c.label} · ${fmtINR(amt)}`} />;
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
